/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Text, useAlerts, ResponsiveContainer, Button, Collapse } from '@theneatcompany/nui'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, useFormikContext, Field } from 'formik'
import { CardElement, useRecurly } from '@recurly/react-recurly'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import PropTypes from 'prop-types'

import Layout from '@/components/Layout/Layout'
import Card from '@/components/common/Card/Card'
import Form from '@/components/Forms/Form'
import InputField from '@/components/Forms/InputField'
import SelectField from '@/components/Forms/SelectField'
import { COUNTRIES, ZIP_CODE_REGEX_CA, ZIP_CODE_REGEX_US, ROUTES, SUBSCRIPTION_STATUSES } from '@/constants'
import Yup from '@/utils/yup'
import { mixed } from '@/translations/validations'
import { formatPrecision, getSelectOptions } from '@/utils/forms'
import CreditCardsSVG from '@/assets/svg/credit-card-logos.svg'
import { useAccounts } from '@/components/Providers/AccountsProvider'
import { UPDATE_PAYMENT_METHOD } from '@/gql/user'
import { INITIAL_LOAD } from '@/gql/common'
import { hasOverdueInvoices } from '@/utils/invoice'
import paypalBuyImg from '@/assets/png/pp-logo-200px.png'
import { sendError } from '@/utils/rollbar'

import styles from './payment-method.module.scss'

const schema = Yup.object().shape({
  first_name: Yup.string().trim().max(25).required(),
  last_name: Yup.string().trim().max(25).required(),
  country: Yup.string().required(),
  postal_code: Yup.string().when('country', {
    is: val => val === 'CA',
    then: Yup.string().matches(ZIP_CODE_REGEX_CA, mixed.default).required(),
    otherwise: Yup.string().matches(ZIP_CODE_REGEX_US, mixed.default).required(),
  }),
  paymentMethod: Yup.string().required(),
})

const initialValues = {
  country: 'US',
  first_name: '',
  last_name: '',
  postal_code: '',
  paymentMethod: 'ccPayment',
}

const PaymentMethods = ({ setRecurlyValid }) => {
  const [isCcOpen, setIsCcOpen] = useState(false)
  const { values } = useFormikContext()

  useEffect(() => {
    if (values.paymentMethod === 'ccPayment') {
      setIsCcOpen(true)
      setRecurlyValid(false)
    } else {
      setIsCcOpen(false)
      setRecurlyValid(true)
    }
  }, [values.paymentMethod])

  return (
    <>
      <div className={styles.paymentMethodButtons}>
        <label className={styles.paymentLabel}>
          <div id="ccPayment" className={styles.paymentRadioButton}>
            <Field type="radio" name="paymentMethod" value="ccPayment" />
            Credit Card
          </div>
        </label>
        <label className={styles.paymentLabel}>
          <div id="paypalPayment" className={styles.paymentRadioButton}>
            <Field type="radio" name="paymentMethod" value="paypalPayment" />
            <Image alt="paypal" style={{ height: 'auto' }} src={paypalBuyImg} width={80} />
          </div>
        </label>
      </div>
      <Collapse isOpen={isCcOpen} className={styles.ccCollapse}>
        <div className={styles.recurly}>
          <div className={styles.cardImages}>
            <div><Image src={CreditCardsSVG} height={30} width={220} alt="PayPal" /></div>
          </div>
          <CardElement onChange={({ valid }) => setRecurlyValid(valid)} />
        </div>
      </Collapse>
    </>
  )
}

PaymentMethods.propTypes = {
  setRecurlyValid: PropTypes.func,
}

export default function PaymentMethodPage() {
  const [isRecurlyValid, setRecurlyValid] = useState(false)
  const [inProgress, setInProgress] = useState(false)

  const formValues = useRef()

  const { formatMessage } = useIntl()
  const recurly = useRecurly()
  const alerts = useAlerts()
  const router = useRouter()
  const { user, subscriptions: [base], invoices, paymentMethod } = useAccounts()
  const isExpired = base?.status === SUBSCRIPTION_STATUSES.EXPIRED
  const [updateAccountUserData] = useLazyQuery(INITIAL_LOAD, {
    fetchPolicy: 'network-only',
  })
  const [updatePaymentMethod] = useMutation(UPDATE_PAYMENT_METHOD, {
    onCompleted: () => alerts.success(formatMessage({ id: 'success.paymentMethod' })),
    onError: () => alerts.error(formatMessage({ id: 'errors.subscribe' })),
  })

  const paypal = recurly.PayPal({
    payPalComplete: true,
  })

  const isUpdatePaymentMethodAllowed = user.flags.find(f => f.name === 'allow_payment_method_creation')?.enabled

  const overdueInvoices = useMemo(() => hasOverdueInvoices(invoices), [invoices])

  useEffect(() => {
    if (isExpired || !user.integrations?.recurlyId) router.replace(ROUTES.overview)
  }, [isExpired, user.integrations])

  paypal.on('error', (err) => {
    sendError(err, 'PayPal subscribe error')
    alerts.error(formatMessage({ id: 'errors.subscribe' }))
    setInProgress(false)
  })

  paypal.on('cancel', () => {
    setInProgress(false)
  })

  paypal.on('token', async (token) => {
    await updatePaymentMethod({
      variables: {
        tokenId: token.id, address: { country: formValues.current.country, postalCode: formValues.current.postal_code },
      },
    })
    await updateAccountUserData()
    setInProgress(false)
  })

  const handleUpdatePaymentMethod = (model) => {
    const values = schema.cast(model)
    formValues.current = values

    setInProgress(true)

    if (values.paymentMethod === 'paypalPayment') {
      paypal.start()
    } else {
      recurly.token(values, async (err, token) => {
        if (err) {
          alerts.error(err.message)
        } else {
          await updatePaymentMethod({
            variables: { tokenId: token.id },
          })
          await updateAccountUserData()
        }
        setInProgress(false)
      })
    }
  }

  // TODO:
  const paymentMethodList = {
    ...(paymentMethod && { type: paymentMethod.type === 'paypalBillingAgreement' ? <Image alt="paypal" style={{ height: 'auto' }} src={paypalBuyImg} width={100} /> : 'Credit Card' }),
    ...(paymentMethod?.type === 'creditCard' && {
      creditCard: `***********${paymentMethod?.details.lastFour}`,
      expires: `${paymentMethod?.details.expMonth}/${paymentMethod?.details.expYear}`,
    }),
  }

  if (isExpired) return null

  return (
    <Layout.Content>
      <Layout.Title messageId="updatePaymentMethod" />
      <div className={styles.topSection}>

        <ResponsiveContainer size="sm">
          <Card>
            <Card.Body className={styles.body}>
              <Formik
                onSubmit={handleUpdatePaymentMethod}
                initialValues={initialValues}
                validationSchema={schema}
              >
                {({ isValid, dirty }) => (
                  <Form className={styles.form}>
                    {isUpdatePaymentMethodAllowed ? (
                      <div className={styles.note}>
                        <Text color="foreground-secondary" size="small" typeStyle="bold"><FormattedMessage id="note" />: </Text>
                        <Text color="foreground-secondary" size="small"><FormattedMessage id="updateCardDetails" /></Text>
                      </div>
                    ) : (
                      <div className={styles.locked}>
                        <Text color="foreground-secondary" size="small">
                          <FormattedMessage id="updatePaymentMethodLocked" />
                        </Text>
                      </div>
                    )}

                    {isUpdatePaymentMethodAllowed && (
                    <>
                      <div className={styles.billingAddressContainer}>
                        <div>
                          <h1 className={styles.paymentHeading}>Billing Address</h1>
                          <div className={styles.billingAddress}>
                            <InputField
                              name="first_name"
                              data-recurly="first_name"
                              labelProps={{ label: formatMessage({ id: 'labels.firstName' }) }}
                            />
                            <InputField
                              name="last_name"
                              data-recurly="last_name"
                              labelProps={{ label: formatMessage({ id: 'labels.lastName' }) }}
                            />
                            <SelectField
                              name="country"
                              data-recurly="country"
                              options={getSelectOptions(COUNTRIES)}
                              labelProps={{ label: formatMessage({ id: 'labels.country' }) }}
                            />
                            <InputField
                              name="postal_code"
                              data-recurly="postal_code"
                              labelProps={{ label: formatMessage({ id: 'labels.postal' }) }}
                            />
                          </div>
                        </div>
                        <h1 className={styles.paymentHeading}>Choose your payment method type</h1>
                        <PaymentMethods setRecurlyValid={setRecurlyValid} />
                      </div>

                      <div className={styles.buttons}>
                        <Button type="submit" disabled={!isValid || !dirty || !isRecurlyValid || inProgress}>
                          <FormattedMessage id="button.update" />
                        </Button>
                      </div>
                    </>
                    )}

                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        </ResponsiveContainer>
        <div className={styles.paymentMethodDefault}>

          {paymentMethod && user.integrations?.recurlyId && (
          <Card className={styles.paymentMethod}>
            <Card.Header messageId="paymentMethod" />
            <Card.Body>
              <Card>
                <Card.Body>
                  <Card.List>
                    {Object.entries(paymentMethodList).map(([key, value]) => (
                      <Card.ListElement key={key} value={value} labelKey={key} />
                    ))}
                  </Card.List>
                </Card.Body>
              </Card>
              <div className={styles.balance}>
                <Text element="p" type="display" size="small" color="foreground-secondary">
                  <FormattedMessage id="currentBalance" />
                </Text>
                <Text type="display" size="large" color="tint-brand-primary">
                  {`$${formatPrecision(user.balance, 2)}`}
                </Text>
                {overdueInvoices &&
                <Text element="p" typeStyle="bold" color="tint-danger-primary">
                  <FormattedMessage id="accountBillingStatus.past_due" />
                </Text>}
              </div>
            </Card.Body>
          </Card>
          )}
        </div>
      </div>
    </Layout.Content>
  )
}
